/* eslint-disable max-statements */
import React, { useRef, useEffect, useState, useCallback } from "react";
import AsyncSelect from "react-select/async";
import { components } from "react-select";
import PropTypes from "prop-types";
import styles from "./style.css";
import { isEmptyValue } from "../../../utils/helpers/finance-util";

// pass isSearchable = false to disable the search
const Search = ({
    data = [],
    defaultMenuIsOpen = false,
    onChange = () => {},
    onBlurChange = () => {},
    placeholder = "Select...",
    customDefaultValue = null,
    autoFocus = false,
    showLogoOptions,
    showError = false,
    tabAccessibility = false,
    validator,
    onMenuOpenCallback = () => {},
    onMenuCloseCallback = () => {},
    defaultInputValue = "",
    disabled = false,
    variant = "default",
    ...restProps
}) => {
    const { form = {}, field = {} } = restProps || {};

    const inputRef = useRef(null); // Will store the DOM input reference

    const [inputValue, setInputValue] = useState(customDefaultValue || defaultInputValue || "");

    useEffect(() => {
        if (autoFocus && inputRef.current) {
            inputRef.current.focus();
        }
    }, [autoFocus]);

    useEffect(() => {
        setInputValue(customDefaultValue || defaultInputValue || "");
    }, [customDefaultValue, defaultInputValue]);

    const filterOptions = useCallback((input) => {
        return new Promise((resolve) => {
            const result = data.filter(({ value }) =>
                value?.toString().toLowerCase().includes(input.toLowerCase())
            );
            resolve(result);
        });
    }, [data]);

    const onInputChange = (v, action) => {
        if (validator && !validator(v)) return null;
        if (action.action === "input-blur") {
            onBlurChange(v, restProps);
        }
        if (action.action !== "input-blur" && action.action !== "menu-close") {
            setInputValue(v);
        }
        return v;
    };

    const Option = (props) => {
        const { logo, label, disabled: disabledProp } = props.data || {};
        return (
            <div styleName={`styles.logoWrapper ${disabledProp ? "styles.disableLogoWrapperEle" : ""}`}>
                <components.Option {...props}>
                    {logo && <img src={logo} alt={label} />}
                    <span>{label}</span>
                </components.Option>
            </div>
        );
    };

    if (showLogoOptions) {
        restProps.components = { ...(restProps.components || {}), Option };
    }

    if (restProps.isFormikForm) {
        const selectedValue = data.find((obj) => obj.value === field.value);
        restProps.value = selectedValue || null;
    }

    const getInputRef = useCallback((ref) => {
        inputRef.current = ref;
    }, []);

    const variantClass = variant === "v3" ? "search-select-v3" : "";

    return (
        <div styleName={showError ? "styles.errorBox" : ""} className={`search-select-container ${variantClass}`}>
            <AsyncSelect
                inputValue={inputValue}
                onInputChange={onInputChange}
                onChange={(value) => {
                    onChange(value, restProps);
                    if (restProps.isFormikForm && form.setFieldValue) {
                        form.setFieldValue(restProps.id, isEmptyValue(value?.value) ? "" : value?.value);
                    }
                }}
                className={`search-select-container`}
                placeholder={placeholder}
                defaultOptions={data}
                loadOptions={filterOptions}
                classNamePrefix="search-select"
                defaultMenuIsOpen={defaultMenuIsOpen}
                onMenuOpen={onMenuOpenCallback}
                onMenuClose={onMenuCloseCallback}
                isDisabled={disabled}
                ref={getInputRef}
                {...(tabAccessibility
                    ? {
                        openMenuOnFocus: true,
                        tabSelectsValue: true
                    }
                    : {})}
                {...restProps}
            />
        </div>
    );
};

Search.propTypes = {
    data: PropTypes.array.isRequired,
    defaultMenuIsOpen: PropTypes.bool,
    showError: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
    onBlurChange: PropTypes.func.isRequired,
    customDefaultValue: PropTypes.string,
    placeholder: PropTypes.string,
    value: PropTypes.any,
    autoFocus: PropTypes.bool,
    onMenuOpenCallback: PropTypes.func,
    onMenuCloseCallback: PropTypes.func,
    showLogoOptions: PropTypes.bool,
    tabAccessibility: PropTypes.bool,
    validator: PropTypes.func,
    defaultInputValue: PropTypes.string,
    disabled: PropTypes.bool,
    variant: PropTypes.oneOf(["default", "v3"])
};

export default Search;
